import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge, StatusDot } from '@/components/atoms';
import { ChartCard } from '@/components/Charts/ChartCard';
import { chartTheme, chartTooltipStyle } from '@/components/Charts/theme';
import { useArticles, useCategories } from '@/hooks/useArticles';
import {
  useOpenAiCostStats,
  usePostsDailyStats,
  useStarredWorkflowsStats,
} from '@/hooks/useStats';
import { useExecutions, useWorkflows } from '@/hooks/useWorkflows';
import { usePersona } from '@/store/usePersona';
import { useStarred } from '@/store/useStarred';
import { PERSONA_LABELS } from '@/components/PersonaSwitcher/types';

import styles from './Dashboard.module.css';

const EDITORIA_CATS = [
  { id: 13, name: 'Esportes' },
  { id: 99, name: 'Tecnologia' },
  { id: 4, name: 'Mercado' },
  { id: 5, name: 'Mundo' },
  { id: 14, name: 'Últimas' },
  { id: 25, name: 'Horóscopo' },
];

function isoToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function shortDay(iso: string): string {
  // 2026-05-21 → 21/05
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function Dashboard() {
  const persona = usePersona((s) => s.persona);
  const starredIds = useStarred((s) => s.ids);

  const { data: workflows, isError: wfError } = useWorkflows();
  const { data: errors } = useExecutions({ status: 'error', limit: 5 });
  const { data: posts } = useArticles({ per_page: 8 });
  const { data: postsToday } = useArticles({ after: isoToday(), per_page: 100 });
  const { data: categories } = useCategories();
  const { data: postsDaily } = usePostsDailyStats(14);
  const { data: openaiStats } = useOpenAiCostStats(30);
  const starredStats = useStarredWorkflowsStats(starredIds);

  const [dailyDays, setDailyDays] = useState<7 | 14 | 30>(14);
  const { data: postsDailyExtended } = usePostsDailyStats(dailyDays);

  const activeCount = workflows?.filter((w) => w.active).length ?? 0;
  const totalCount = workflows?.length ?? 0;

  const editoriaTotalByName = useMemo(() => {
    if (!categories) return new Map<number, number>();
    return new Map(categories.map((c) => [c.id, c.count]));
  }, [categories]);

  const dailyData = (postsDailyExtended?.days ?? postsDaily?.days ?? []).map((p) => ({
    day: shortDay(p.day),
    posts: p.total,
  }));

  const editoriaData = EDITORIA_CATS.map((cat) => ({
    name: cat.name,
    value: editoriaTotalByName.get(cat.id) ?? 0,
  })).filter((d) => d.value > 0);

  const openaiData = (openaiStats?.by_day ?? []).map((p) => ({
    day: shortDay(p.day),
    cost: Number(p.cost_usd.toFixed(4)),
    calls: p.calls,
  }));

  const successData = starredStats.data.map(({ id, stats }) => {
    const workflowName =
      workflows?.find((w) => w.id === id)?.name ?? id;
    const shortName =
      workflowName.length > 24 ? `${workflowName.slice(0, 22)}…` : workflowName;
    return {
      name: shortName,
      success: stats.success_count,
      error: stats.error_count,
      rate: Math.round(stats.success_rate * 100),
    };
  });

  const services = [
    { name: 'PostgreSQL', state: 'ok' as const, detail: 'Database conectado' },
    {
      name: 'WordPress',
      state: posts ? ('ok' as const) : ('off' as const),
      detail: posts
        ? `${posts.total.toLocaleString('pt-BR')} artigos no arquivo`
        : 'Sem dados',
    },
    {
      name: 'N8N',
      state: wfError ? ('crit' as const) : ('ok' as const),
      detail: wfError ? 'Erro na lista' : `${totalCount} workflows visíveis`,
    },
    {
      name: 'OpenAI',
      state: openaiStats && openaiStats.total_calls > 0
        ? ('ok' as const)
        : ('off' as const),
      detail: openaiStats
        ? `${openaiStats.total_calls} chamadas · $${openaiStats.total_cost_usd.toFixed(4)}`
        : 'Sem chamadas',
    },
    { name: 'Twitter / X', state: 'off' as const, detail: 'Read-only via WP meta' },
  ];

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>
            Visão activa:{' '}
            <Badge tone="info" size="xs">
              {PERSONA_LABELS[persona]}
            </Badge>
          </p>
        </div>
      </header>

      <div className={styles.grid}>
        <div className={styles.card}>
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Workflows</h2>
            <Link to="/workflows" className={styles.cardLink}>
              ver todos →
            </Link>
          </header>
          <div className={styles.metric}>
            <span className={styles.metricNumber}>
              {activeCount} / {totalCount}
            </span>
            <span className={styles.metricLabel}>activos</span>
          </div>
          <div className={styles.statusRow}>
            <StatusDot
              state={
                wfError
                  ? 'crit'
                  : activeCount === totalCount && totalCount > 0
                    ? 'ok'
                    : 'warn'
              }
            />
            <span className={styles.muted}>
              {wfError
                ? 'Falha N8N'
                : activeCount === totalCount
                  ? 'Tudo a correr'
                  : `${totalCount - activeCount} pausado(s)`}
            </span>
          </div>
        </div>

        <div className={styles.card}>
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Publicações hoje</h2>
            <Link to="/artigos" className={styles.cardLink}>
              ver todos →
            </Link>
          </header>
          <div className={styles.metric}>
            <span className={styles.metricNumber}>{postsToday?.total ?? 0}</span>
            <span className={styles.metricLabel}>artigos</span>
          </div>
          <div className={styles.statusRow}>
            <StatusDot state="ok" />
            <span className={styles.muted}>WordPress respondendo</span>
          </div>
        </div>

        <div className={styles.card}>
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Erros recentes</h2>
            <Link to="/alertas" className={styles.cardLink}>
              ver tudo →
            </Link>
          </header>
          {(!errors || errors.length === 0) && (
            <div className={styles.statusRow}>
              <StatusDot state="ok" />
              <span className={styles.muted}>Sem erros</span>
            </div>
          )}
          {errors && errors.length > 0 && (
            <ul className={styles.errorList}>
              {errors.slice(0, 3).map((e) => (
                <li key={e.id} className={styles.errorItem}>
                  <StatusDot state="crit" />
                  <span>
                    {e.workflow_id.slice(0, 14)} —{' '}
                    {new Date(e.started_at).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ─── Charts ──────────────────────────────────────────────────────── */}
      <div className={styles.chartsGrid}>
        <ChartCard
          title="Publicações por dia"
          subtitle={`Total na janela: ${postsDailyExtended?.total_in_window ?? postsDaily?.total_in_window ?? 0}`}
          action={
            <div className={styles.rangeToggle} role="group">
              {([7, 14, 30] as const).map((d) => (
                <button
                  key={d}
                  className={`${styles.rangeBtn} ${dailyDays === d ? styles.rangeBtnActive : ''}`}
                  onClick={() => setDailyDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
          }
        >
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="postsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartTheme.brand} stopOpacity={0.5} />
                    <stop offset="95%" stopColor={chartTheme.brand} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={chartTooltipStyle} cursor={{ stroke: chartTheme.brand }} />
                <Area
                  type="monotone"
                  dataKey="posts"
                  stroke={chartTheme.brand}
                  strokeWidth={2}
                  fill="url(#postsGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>Sem dados ainda…</p>
          )}
        </ChartCard>

        <ChartCard
          title="Distribuição por editoria"
          subtitle={`${editoriaData.length} editorias com publicações`}
        >
          {editoriaData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={editoriaData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke={chartTheme.background}
                  strokeWidth={2}
                >
                  {editoriaData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={chartTheme.series[i % chartTheme.series.length]}
                    />
                  ))}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{
                    fontSize: 11,
                    fontFamily: 'Inter, sans-serif',
                    color: chartTheme.text,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>Sem dados ainda…</p>
          )}
        </ChartCard>

        <ChartCard
          title="Workflows favoritados — sucesso/erro"
          subtitle={
            starredIds.length === 0
              ? 'Usa "Favoritar" em workflows para popular este card'
              : `${starredIds.length} workflows starred · amostra 50 execuções cada`
          }
          height={Math.max(180, 50 + successData.length * 40)}
        >
          {successData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={successData}
                layout="vertical"
                margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} horizontal={false} />
                <XAxis
                  type="number"
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke={chartTheme.axis}
                  fontSize={11}
                  width={150}
                  tickLine={false}
                />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.text }} />
                <Bar dataKey="success" stackId="a" fill={chartTheme.ok} name="success" />
                <Bar dataKey="error" stackId="a" fill={chartTheme.crit} name="erro" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>
              Nenhum workflow favoritado. Usa "Favoritar" em /workflows para começar.
            </p>
          )}
        </ChartCard>

        <ChartCard
          title="Custo OpenAI (testes de template)"
          subtitle={
            openaiStats
              ? `Total 30d: $${openaiStats.total_cost_usd.toFixed(4)} · ${openaiStats.total_calls} chamadas`
              : 'A carregar…'
          }
        >
          {openaiData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={openaiData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                <XAxis dataKey="day" stroke={chartTheme.axis} fontSize={11} tickLine={false} />
                <YAxis
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke={chartTheme.warn}
                  strokeWidth={2}
                  dot={{ r: 3, fill: chartTheme.warn }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>
              Sem chamadas registadas. Testa um template em /config/prompts/:id.
            </p>
          )}
        </ChartCard>
      </div>

      {/* ─── Health services ──────────────────────────────────────────── */}
      <div className={styles.card} style={{ marginTop: 'var(--sp-5)' }}>
        <header className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Saúde dos serviços externos</h2>
        </header>
        <ul className={styles.servicesList}>
          {services.map((svc) => (
            <li key={svc.name} className={styles.serviceRow}>
              <StatusDot state={svc.state} />
              <strong className={styles.serviceName}>{svc.name}</strong>
              <span className={styles.serviceDetail}>{svc.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.card} style={{ marginTop: 'var(--sp-5)' }}>
        <header className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Últimas publicações</h2>
          <Link to="/artigos" className={styles.cardLink}>
            ver todas →
          </Link>
        </header>
        {posts && posts.items.length === 0 && (
          <p className={styles.muted}>Nada por aqui ainda.</p>
        )}
        {posts && posts.items.length > 0 && (
          <ul className={styles.list}>
            {posts.items.map((p) => (
              <li key={p.id} className={styles.listRow}>
                <Link to={`/artigos/${p.id}`} className={styles.listLink}>
                  {p.title}
                </Link>
                <span className={styles.listMeta}>
                  {new Date(p.published_at).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
